import { supabase } from "@/integrations/supabase/client";
import { listPosts as listSeedPosts, type BlogPost as SeedPost } from "@/content/blog";

export interface DbBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body_markdown: string;
  cover_image_url: string | null;
  author: string;
  tags: string[];
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UnifiedPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string; // ISO YYYY-MM-DD
  author: string;
  coverImage: string;
  tags?: string[];
  /** When set, render markdown. Otherwise fall back to seed `body` blocks. */
  bodyMarkdown?: string;
  bodyBlocks?: SeedPost["body"];
}

function dbToUnified(p: DbBlogPost): UnifiedPost {
  return {
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    date: (p.published_at ?? p.created_at).slice(0, 10),
    author: p.author,
    coverImage: p.cover_image_url ?? "",
    tags: p.tags,
    bodyMarkdown: p.body_markdown,
  };
}

function seedToUnified(p: SeedPost): UnifiedPost {
  return {
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    date: p.date,
    author: p.author,
    coverImage: p.coverImage,
    tags: p.tags,
    bodyBlocks: p.body,
  };
}

/** All published posts, DB first, then legacy seed posts (deduped by slug). */
export async function fetchPublishedPosts(): Promise<UnifiedPost[]> {
  const { data } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("published", true)
    .order("published_at", { ascending: false });

  const dbPosts = (data ?? []).map(dbToUnified);
  const seedPosts = listSeedPosts().map(seedToUnified);
  const seen = new Set(dbPosts.map((p) => p.slug));
  const merged = [...dbPosts, ...seedPosts.filter((p) => !seen.has(p.slug))];
  return merged.sort((a, b) => (a.date < b.date ? 1 : -1));
}

export async function fetchPostBySlug(slug: string): Promise<UnifiedPost | null> {
  const { data } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  if (data) return dbToUnified(data as DbBlogPost);
  const seed = listSeedPosts().find((p) => p.slug === slug);
  return seed ? seedToUnified(seed) : null;
}
